import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Eye,
  FileBarChart2,
  Inbox,
  LayoutDashboard,
  Megaphone,
  MessageSquareText,
  PhoneCall,
  Plus,
  ShieldCheck,
  Target,
  TrendingDown,
  TrendingUp,
  UserRoundCheck,
  Users,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import { usePartners } from "@/context/PartnerContext";
import { useLeadMetrics } from "@/hooks/useLeadMetrics";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AdminEmptyState, AdminErrorState, AdminMetricSkeletons } from "@/components/admin/AdminDataState";
import { cn } from "@/lib/utils";

const formatNumber = new Intl.NumberFormat("el-GR").format;

const RANGE_LABELS: Record<string, string> = {
  "7d": "Τελευταίες 7 ημέρες",
  "30d": "Τελευταίες 30 ημέρες",
  "3m": "Τελευταίοι 3 μήνες",
  "1y": "Τελευταίος χρόνος",
};

const STAGE_META: Record<string, { label: string; color: string; bar: string }> = {
  Νέο: { label: "Νέα", color: "bg-sky-50 text-sky-700 ring-sky-100", bar: "bg-sky-500" },
  σεΕκκρεμότητα: { label: "Σε εκκρεμότητα", color: "bg-amber-50 text-amber-700 ring-amber-100", bar: "bg-amber-500" },
  ήθελεΠληροφορίες: { label: "Πληροφορίες", color: "bg-violet-50 text-violet-700 ring-violet-100", bar: "bg-violet-500" },
  έκλεισεΗΔουλειά: { label: "Επιτυχία", color: "bg-emerald-50 text-emerald-700 ring-emerald-100", bar: "bg-emerald-500" },
  δενΑπάντησε: { label: "Δεν απάντησε", color: "bg-slate-100 text-slate-700 ring-slate-200", bar: "bg-slate-400" },
  αδύνατηΕπικοινωνία: { label: "Αδύνατη επικοινωνία", color: "bg-rose-50 text-rose-700 ring-rose-100", bar: "bg-rose-500" },
};

const SERVICE_LABELS: Record<string, string> = {
  εξοικονομώ: "Εξοικονομώ",
  αντλίεςΘερμότητας: "Αντλίες θερμότητας",
  φωτοβολταϊκά: "Φωτοβολταϊκά",
  κλιματιστικά: "Κλιματιστικά",
  φυσικόΑέριο: "Φυσικό αέριο",
  ηλιακά: "Ηλιακά",
  ανακαινίζωΕνεργειακά: "Ανακαινίζω - Ενεργειακά συστήματα",
  ανακαινίζωΠλήρηςΑνακαίνιση: "Ανακαινίζω - Πλήρης ανακαίνιση",
  άλλο: "Άλλο",
};

const SOURCE_LABELS: Record<string, string> = {
  facebook_api: "Meta API · Realtime",
  facebook_sheets: "Facebook Sheets",
  facebook_leads: "Facebook Ads",
  web: "Φόρμα ιστοσελίδας",
  phone: "Τηλέφωνο",
  manual: "Χειροκίνητα",
  demo: "Demo",
};

const QUICK_ACTIONS: Array<{ label: string; description: string; path: string; icon: LucideIcon }> = [
  { label: "Όλα τα leads", description: "Αναζήτηση & φίλτρα", path: "/admin/leads", icon: Inbox },
  { label: "Νέο lead", description: "Χειροκίνητη καταχώρηση", path: "/admin/leads/new", icon: Plus },
  { label: "Operations", description: "Εξαιρέσεις & SLA", path: "/admin/operations", icon: ShieldCheck },
  { label: "Αναφορές", description: "Απόδοση & κανάλια", path: "/admin/reports", icon: FileBarChart2 },
];

const safeRelativeTime = (value: string): string => {
  try {
    return formatDistanceToNow(parseISO(value.replace(" ", "T")), { addSuffix: true, locale: el });
  } catch {
    return "πρόσφατα";
  }
};

function SectionHeading({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="text-lg font-semibold tracking-[-0.015em] text-slate-950">{title}</h2>
        <p className="mt-0.5 text-sm leading-6 text-slate-500">{description}</p>
      </div>
      {action}
    </div>
  );
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { partners } = usePartners();
  const [timeRange, setTimeRange] = useState("30d");
  const { metrics, loading, error, refetch } = useLeadMetrics(timeRange);

  const activePartners = useMemo(() => partners.filter((partner) => partner.active).length, [partners]);

  const summary = useMemo(() => {
    const total = Number(metrics.summary.total || 0);
    const assigned = Math.max(0, total - Number(metrics.summary.unassigned || 0));
    const assignmentRate = total > 0 ? Math.round((assigned / total) * 100) : 0;
    const viewRate = assigned > 0 ? Math.round((Number(metrics.summary.viewed || 0) / assigned) * 100) : 0;
    const successRate = total > 0 ? Math.round((Number(metrics.summary.success || 0) / total) * 100) : 0;
    const previous = Number(metrics.comparison.previous || 0);
    const periodDelta = previous > 0
      ? Math.round(((Number(metrics.comparison.current || 0) - previous) / previous) * 100)
      : null;
    return { total, assigned, assignmentRate, viewRate, successRate, periodDelta };
  }, [metrics.comparison, metrics.summary]);

  const weeklyPulse = useMemo(() => {
    const current = Number(metrics.weekly_pulse.current || 0);
    const previous = Number(metrics.weekly_pulse.previous || 0);
    const delta = previous > 0 ? Math.round(((current - previous) / previous) * 100) : null;
    return { current, previous, delta };
  }, [metrics.weekly_pulse]);

  const operations = useMemo(() => [
    {
      key: "unassigned",
      label: "Χωρίς συνεργάτη",
      description: "Χρειάζονται χειροκίνητη ανάθεση ή έλεγχο κάλυψης.",
      count: Number(metrics.summary.unassigned || 0),
      icon: UserRoundCheck,
      tone: "amber" as const,
    },
    {
      key: "stale",
      label: "Εκκρεμότητα πάνω από 48 ώρες",
      description: "Δεν έχει καταγραφεί έγκαιρη εξέλιξη σταδίου.",
      count: Number(metrics.summary.stale_pending || 0),
      icon: Clock3,
      tone: "orange" as const,
    },
    {
      key: "failed-sms",
      label: "Οριστικές αποτυχίες SMS",
      description: "Τα retries ολοκληρώθηκαν χωρίς επιτυχή αποστολή.",
      count: Number(metrics.summary.sms_failed || 0),
      icon: MessageSquareText,
      tone: "rose" as const,
    },
  ], [metrics.summary]);

  const activeOperations = operations.filter((item) => item.count > 0);
  const totalAlerts = activeOperations.reduce((sum, item) => sum + item.count, 0);

  const dailyTrend = useMemo(
    () => metrics.daily.slice(-14).map((row) => ({
      date: format(parseISO(row.date), "d MMM", { locale: el }),
      leads: Number(row.total || 0),
      assigned: Number(row.assigned || 0),
    })),
    [metrics.daily],
  );

  const stageData = useMemo(
    () => metrics.stage
      .filter((row) => Number(row.total || 0) > 0)
      .map((row) => ({
        key: row.name,
        label: STAGE_META[row.name]?.label || row.name || "Άλλο",
        total: Number(row.total || 0),
        bar: STAGE_META[row.name]?.bar || "bg-slate-400",
      }))
      .sort((a, b) => b.total - a.total),
    [metrics.stage],
  );

  const services = useMemo(
    () => metrics.service
      .map((row) => ({
        label: SERVICE_LABELS[row.name] || row.name,
        total: Number(row.total || 0),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5),
    [metrics.service],
  );

  const sources = useMemo(
    () => metrics.source
      .map((row) => ({
        label: SOURCE_LABELS[row.name] || row.name,
        total: Number(row.total || 0),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 4),
    [metrics.source],
  );

  const campaigns = useMemo(
    () => metrics.campaign
      .map((row) => ({ name: row.name, total: Number(row.total || 0) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 3),
    [metrics.campaign],
  );

  if (loading) {
    return (
      <div className="space-y-7 animate-fade-in">
        <PageHeader
          title="Πίνακας ελέγχου"
          description="Φορτώνουμε την τρέχουσα επιχειρησιακή εικόνα."
          icon={<LayoutDashboard />}
          iconGradient="blue"
          eyebrow="Κέντρο λειτουργίας"
        />
        <AdminMetricSkeletons count={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-7 animate-fade-in">
        <PageHeader
          title="Πίνακας ελέγχου"
          description="Η καθημερινή εικόνα εισερχόμενων leads και εργασιών."
          icon={<LayoutDashboard />}
          iconGradient="blue"
          eyebrow="Κέντρο λειτουργίας"
        />
        <AdminErrorState message={error} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Πίνακας ελέγχου"
        description="Τι χρειάζεται ενέργεια σήμερα, πώς κινείται η ζήτηση και αν κάθε νέο lead εξυπηρετείται σωστά."
        icon={<LayoutDashboard />}
        iconGradient="blue"
        eyebrow="Κέντρο λειτουργίας"
        actions={
          <>
            <Button className="min-h-11 flex-1 bg-slate-950 hover:bg-slate-800 sm:flex-none" onClick={() => navigate("/admin/leads/new")}>
              <Plus className="mr-2 h-4 w-4" /> Νέο lead
            </Button>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="h-11 flex-1 border-slate-200 bg-white sm:w-56 sm:flex-none">
                <CalendarDays className="mr-2 h-4 w-4 text-slate-400" />
                <SelectValue placeholder="Περίοδος" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Τελευταίες 7 ημέρες</SelectItem>
                <SelectItem value="30d">Τελευταίες 30 ημέρες</SelectItem>
                <SelectItem value="3m">Τελευταίοι 3 μήνες</SelectItem>
                <SelectItem value="1y">Τελευταίος χρόνος</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
      />

      <section className="relative overflow-hidden rounded-3xl bg-slate-950 p-5 text-white shadow-[0_16px_45px_rgba(15,23,42,0.16)] sm:p-7">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full border border-white/10" />
        <div className="pointer-events-none absolute -right-6 -top-10 h-40 w-40 rounded-full border border-sky-400/20" />
        <div className="relative grid gap-7 lg:grid-cols-[1.25fr_0.75fr] lg:items-start">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-sky-300">
              <Zap className="h-4 w-4" /> Εβδομαδιαίος παλμός
            </div>
            <div className="mt-4 flex flex-wrap items-end gap-x-4 gap-y-2">
              <p className="text-5xl font-bold tabular-nums tracking-[-0.055em] sm:text-6xl">{formatNumber(weeklyPulse.current)}</p>
              <div className="pb-1.5">
                <p className="text-sm font-medium text-slate-200">νέα leads σε 7 ημέρες</p>
                {weeklyPulse.delta !== null && (
                  <p className={cn("mt-1 inline-flex items-center gap-1 text-xs font-semibold", weeklyPulse.delta >= 0 ? "text-emerald-300" : "text-rose-300")}>
                    {weeklyPulse.delta >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                    {weeklyPulse.delta >= 0 ? "+" : ""}{weeklyPulse.delta}% από την προηγούμενη εβδομάδα
                  </p>
                )}
              </div>
            </div>
            <p className="mt-4 max-w-xl text-sm leading-6 text-slate-400">
              Σήμερα καταγράφηκαν <strong className="font-semibold text-white">{formatNumber(metrics.summary.today)}</strong> νέα leads.
              {totalAlerts > 0
                ? ` Υπάρχουν ${formatNumber(totalAlerts)} σημεία που χρειάζονται έλεγχο.`
                : " Δεν υπάρχει ανοιχτή επιχειρησιακή εξαίρεση στα βασικά signals."}
            </p>

            <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {QUICK_ACTIONS.map(({ label, description, path, icon: Icon }) => (
                <button
                  key={path}
                  type="button"
                  onClick={() => navigate(path)}
                  className="group min-h-20 rounded-2xl border border-white/10 bg-white/[0.055] p-3 text-left transition-colors hover:border-white/20 hover:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
                >
                  <Icon className="h-4 w-4 text-sky-300" />
                  <p className="mt-2 text-xs font-semibold text-white">{label}</p>
                  <p className="mt-0.5 hidden text-[11px] leading-4 text-slate-400 sm:block">{description}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-2 rounded-2xl border border-white/10 bg-white/[0.045] p-3.5 sm:grid-cols-3 lg:grid-cols-1">
            {[
              { label: "Σήμερα", value: metrics.summary.today, helper: "νέα leads", icon: PhoneCall },
              { label: "Ενεργοί συνεργάτες", value: activePartners, helper: `${partners.length} συνολικά`, icon: Users },
              { label: "Operations", value: totalAlerts, helper: totalAlerts > 0 ? "χρειάζονται έλεγχο" : "όλα καθαρά", icon: AlertTriangle },
            ].map(({ label, value, helper, icon: Icon }) => (
              <div key={label} className="flex min-h-16 items-center gap-3 rounded-xl px-2.5 py-2 sm:block lg:flex">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-sky-200">
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <div className="min-w-0 sm:mt-2 lg:mt-0">
                  <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">{label}</p>
                  <p className="mt-0.5 text-lg font-bold tabular-nums text-white">{formatNumber(Number(value || 0))}</p>
                  <p className="truncate text-[11px] text-slate-500">{helper}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard
          title="Leads περιόδου"
          value={formatNumber(summary.total)}
          description={RANGE_LABELS[timeRange]}
          icon={<PhoneCall />}
          variant="info"
          size="compact"
          trend={summary.periodDelta === null ? undefined : { value: summary.periodDelta, direction: summary.periodDelta >= 0 ? "up" : "down" }}
        />
        <StatCard
          title="Κάλυψη ανάθεσης"
          value={`${summary.assignmentRate}%`}
          description={`${formatNumber(summary.assigned)} με συνεργάτη`}
          icon={<UserRoundCheck />}
          variant={summary.assignmentRate >= 95 ? "success" : "warning"}
          size="compact"
        />
        <StatCard
          title="Lead link clicks"
          value={`${summary.viewRate}%`}
          description={`${formatNumber(metrics.summary.viewed)} προβολές`}
          icon={<Eye />}
          variant="gradient"
          size="compact"
        />
        <StatCard
          title="Καταγεγραμμένες επιτυχίες"
          value={formatNumber(metrics.summary.success)}
          description={`${summary.successRate}% · βάσει stages`}
          icon={<CheckCircle2 />}
          variant="success"
          size="compact"
        />
      </div>

      <section className="space-y-4">
        <SectionHeading
          title="Τι χρειάζεται ενέργεια"
          description="Οι εξαιρέσεις που μπορούν να καθυστερήσουν ή να αφήσουν lead χωρίς σωστή εξυπηρέτηση."
          action={
            <Button variant="outline" className="min-h-11" onClick={() => navigate("/admin/operations")}>
              Operations Inbox <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          }
        />

        <div className="grid gap-4 lg:grid-cols-[0.85fr_1.35fr]">
          <Card className="border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4.5 w-4.5 text-slate-600" /> Ουρά ελέγχου
                {totalAlerts > 0 && <Badge className="ml-auto bg-rose-600 text-white hover:bg-rose-600">{formatNumber(totalAlerts)}</Badge>}
              </CardTitle>
              <CardDescription>Μόνο signals που απαιτούν ανθρώπινη απόφαση.</CardDescription>
            </CardHeader>
            <CardContent>
              {activeOperations.length === 0 ? (
                <div className="flex min-h-44 flex-col items-center justify-center rounded-2xl bg-emerald-50/70 px-5 text-center ring-1 ring-emerald-100">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                  <p className="mt-3 text-sm font-semibold text-emerald-900">Δεν υπάρχει άμεση εκκρεμότητα</p>
                  <p className="mt-1 text-xs leading-5 text-emerald-700">Τα βασικά assignment, SLA και SMS signals είναι καθαρά.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activeOperations.map(({ key, label, description, count, icon: Icon, tone }) => {
                    const toneClasses = {
                      amber: "border-amber-200 bg-amber-50/70 text-amber-800",
                      orange: "border-orange-200 bg-orange-50/70 text-orange-800",
                      rose: "border-rose-200 bg-rose-50/70 text-rose-800",
                    }[tone];
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => navigate("/admin/operations")}
                        className={cn("group flex min-h-20 w-full items-center gap-3 rounded-2xl border p-3.5 text-left transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500", toneClasses)}
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 ring-1 ring-black/5">
                          <Icon className="h-4.5 w-4.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold">{label}</p>
                          <p className="mt-0.5 text-xs leading-5 opacity-80">{description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold tabular-nums">{formatNumber(count)}</span>
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
              <div>
                <CardTitle className="text-base">Ροή εισερχόμενων leads</CardTitle>
                <CardDescription>Τελευταίες έως 14 ημέρες μέσα στην επιλεγμένη περίοδο.</CardDescription>
              </div>
              <div className="hidden text-right sm:block">
                <p className="text-xs text-slate-400">Περίοδος vs προηγούμενη</p>
                <p className={cn("mt-1 text-sm font-semibold tabular-nums", summary.periodDelta === null ? "text-slate-500" : summary.periodDelta >= 0 ? "text-emerald-700" : "text-rose-700")}>
                  {summary.periodDelta === null ? "—" : `${summary.periodDelta >= 0 ? "+" : ""}${summary.periodDelta}%`}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              {dailyTrend.length === 0 ? (
                <AdminEmptyState title="Δεν υπάρχει ακόμη ημερήσια τάση" description="Η τάση θα εμφανιστεί μόλις υπάρχουν leads στην περίοδο." illustration="lead-insights" />
              ) : (
                <>
                  <div className="mb-3 flex items-center gap-4 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-sky-600" /> Leads</span>
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Ανατεθειμένα</span>
                  </div>
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dailyTrend} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
                        <defs>
                          <linearGradient id="dashboardLeadArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0284c7" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#0284c7" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" tickLine={false} axisLine={false} minTickGap={24} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(15,23,42,.08)" }} />
                        <Area type="monotone" dataKey="leads" name="Leads" stroke="#0284c7" strokeWidth={2.25} fill="url(#dashboardLeadArea)" />
                        <Area type="monotone" dataKey="assigned" name="Ανατεθειμένα" stroke="#10b981" strokeWidth={2} fillOpacity={0} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeading
          title="Τρέχουσα εικόνα"
          description="Τα τελευταία εισερχόμενα και η κατανομή των ενημερωμένων σταδίων."
        />
        <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)]">
          <Card className="min-w-0 border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
              <div>
                <CardTitle className="text-base">Πρόσφατα leads</CardTitle>
                <CardDescription>Οι τελευταίες έξι επαφές από όλες τις πηγές.</CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="text-sky-700" onClick={() => navigate("/admin/leads")}>
                Όλα <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </CardHeader>
            <CardContent className="min-w-0">
              {metrics.recent.length === 0 ? (
                <AdminEmptyState title="Δεν υπάρχουν πρόσφατα leads" description="Οι νέες επαφές θα εμφανίζονται εδώ αυτόματα." illustration="lead-flow" />
              ) : (
                <div className="divide-y divide-slate-100">
                  {metrics.recent.map((lead) => {
                    const stage = STAGE_META[lead.stage] || { label: lead.stage || "Άγνωστο", color: "bg-slate-100 text-slate-700 ring-slate-200" };
                    const partnerName = lead.partner_name || (lead.partner_id ? "Ανατεθειμένο" : null);
                    return (
                      <button
                        key={lead.id}
                        type="button"
                        onClick={() => navigate(`/admin/lead/${lead.id}`)}
                        className="group flex min-h-18 w-full items-center gap-3 py-3 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500 sm:px-2"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sm font-bold text-sky-700 ring-1 ring-sky-100">
                          {(lead.name || "?").trim().charAt(0).toUpperCase() || "?"}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-900 group-hover:text-sky-800">{lead.name || "Χωρίς όνομα"}</p>
                          <p className="mt-0.5 truncate text-xs text-slate-500">
                            {SERVICE_LABELS[lead.service] || lead.service || "Άλλη υπηρεσία"}
                            <span className={cn("ml-1.5", partnerName ? "text-slate-400" : "font-medium text-amber-700")}>
                              · {partnerName || "Χωρίς συνεργάτη"}
                            </span>
                          </p>
                        </div>
                        <div className="hidden text-right sm:block">
                          <span className={cn("inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1", stage.color)}>{stage.label}</span>
                          <p className="mt-1 text-[11px] text-slate-400">{safeRelativeTime(lead.created_at)}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-sky-600" />
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="min-w-0 border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base"><Target className="h-4.5 w-4.5 text-violet-600" /> Pipeline snapshot</CardTitle>
              <CardDescription>Κατανομή τρεχόντων stages — όχι διαδοχικό funnel.</CardDescription>
            </CardHeader>
            <CardContent className="min-w-0">
              <div className="mb-5 grid grid-cols-2 gap-2 rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">Μέση ανάθεση</p>
                  <p className="mt-1 text-xl font-bold tabular-nums text-slate-950">{metrics.summary.avg_assignment_hours}ω</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">Νέα χωρίς stage</p>
                  <p className="mt-1 text-xl font-bold tabular-nums text-slate-950">{formatNumber(metrics.summary.new_leads)}</p>
                </div>
              </div>
              <div className="space-y-3.5">
                {stageData.map((stage) => {
                  const percentage = summary.total > 0 ? Math.round((stage.total / summary.total) * 100) : 0;
                  return (
                    <button key={stage.key} type="button" onClick={() => navigate(`/admin/leads?stage=${encodeURIComponent(stage.key)}`)} className="group w-full text-left">
                      <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
                        <span className="truncate font-medium text-slate-600 group-hover:text-slate-900">{stage.label}</span>
                        <span className="shrink-0 font-semibold tabular-nums text-slate-800">{formatNumber(stage.total)} · {percentage}%</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                        <div className={cn("h-full rounded-full transition-[width] duration-300", stage.bar)} style={{ width: `${percentage}%` }} />
                      </div>
                    </button>
                  );
                })}
              </div>
              <Button variant="outline" className="mt-5 min-h-11 w-full" onClick={() => navigate("/admin/reports/partners")}>
                Αναφορές συνεργατών <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeading
          title="Ζήτηση & προέλευση"
          description="Ποια προϊόντα ζητούνται και ποια κανάλια τροφοδοτούν την επιλεγμένη περίοδο."
          action={
            <Button variant="ghost" className="min-h-11 text-sky-700" onClick={() => navigate("/admin/reports")}>
              Πλήρεις αναφορές <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          }
        />
        <div className="grid min-w-0 gap-4 lg:grid-cols-2">
          <Card className="min-w-0 border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Υπηρεσίες με ζήτηση</CardTitle>
              <CardDescription>Μερίδιο επί των leads της περιόδου.</CardDescription>
            </CardHeader>
            <CardContent className="min-w-0">
              {services.length === 0 ? (
                <AdminEmptyState title="Δεν υπάρχουν υπηρεσίες" description="Δεν βρέθηκαν leads στην επιλεγμένη περίοδο." />
              ) : (
                <div className="space-y-4">
                  {services.map((service) => {
                    const percentage = summary.total > 0 ? Math.round((service.total / summary.total) * 100) : 0;
                    return (
                      <div key={service.label}>
                        <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                          <span className="truncate font-medium text-slate-700">{service.label}</span>
                          <span className="shrink-0 font-semibold tabular-nums text-slate-900">{formatNumber(service.total)} <span className="font-normal text-slate-400">({percentage}%)</span></span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-sky-600" style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="min-w-0 border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base"><Megaphone className="h-4.5 w-4.5 text-violet-600" /> Κανάλια & καμπάνιες</CardTitle>
              <CardDescription>Συνοπτική εικόνα acquisition χωρίς να μετατρέπεται το dashboard σε report.</CardDescription>
            </CardHeader>
            <CardContent className="min-w-0">
              {sources.length === 0 ? (
                <AdminEmptyState title="Δεν υπάρχουν πηγές" description="Δεν βρέθηκαν leads στην επιλεγμένη περίοδο." />
              ) : (
                <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-5 sm:grid-cols-2">
                  <div className="min-w-0">
                    <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400">Πηγές</p>
                    <div className="space-y-2.5">
                      {sources.map((source, index) => (
                        <div key={source.label} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5 ring-1 ring-slate-100">
                          <span className="flex min-w-0 items-center gap-2 text-xs font-medium text-slate-700">
                            <span className={cn("h-2 w-2 shrink-0 rounded-full", index === 0 ? "bg-violet-500" : index === 1 ? "bg-sky-500" : "bg-slate-400")} />
                            <span className="truncate">{source.label}</span>
                          </span>
                          <span className="shrink-0 text-xs font-bold tabular-nums text-slate-900">{formatNumber(source.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400">Top καμπάνιες</p>
                    {campaigns.length === 0 ? (
                      <p className="rounded-xl bg-slate-50 px-3 py-4 text-xs leading-5 text-slate-500 ring-1 ring-slate-100">Δεν υπάρχουν campaign labels στην περίοδο.</p>
                    ) : (
                      <div className="space-y-2.5">
                        {campaigns.map((campaign, index) => (
                          <div key={campaign.name} className="flex items-start gap-3 rounded-xl border border-slate-100 px-3 py-2.5">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-[11px] font-bold text-violet-700">{index + 1}</span>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-medium text-slate-700" title={campaign.name}>{campaign.name}</p>
                              <p className="mt-0.5 text-[11px] font-semibold tabular-nums text-slate-950">{formatNumber(campaign.total)} leads</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
